import { jwtDecode } from "jwt-decode";
import { APPLICATION_NAME } from "./constants";

export function logoutUser() {
    if (typeof window === "undefined") return;
    // Always purge the stale session, even when we're already on /login, so an
    // expired/invalid token can never linger and bounce the router into a loop.
    localStorage.clear();
    if (window.location.pathname !== "/login") {
        window.location.href = "/login";
    }
}

export function isTokenExpired() {
    const token = localStorage.getItem("token");
    if (!token) return true;
    try {
        const { exp } = jwtDecode(token);
        if (!exp) return false;
        return Date.now() >= exp * 1000;
    } catch {
        return true;
    }
}

// Single source of truth for "is the user logged in". The auth flag alone is
// not enough — an expired token must be treated as logged out, otherwise the
// router keeps redirecting a stale session into protected routes.
export function isAuthenticated() {
    let auth = false;
    try {
        auth = JSON.parse(localStorage.getItem("auth"));
    } catch {
        auth = false;
    }
    return Boolean(auth) && !isTokenExpired();
}
function getToken() {
    return localStorage.getItem("token");
}

function getHeaders(config={}, isFormData=false) {
    if (!config || Object.keys(config).length) {
        config = {headers: {}};
    }
    let headers = config.headers || {}, token = getToken();

    headers['X-Application'] = APPLICATION_NAME.toUpperCase();

    if (!isFormData) {
        headers['Content-Type'] = "application/json";
    }

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
}

export default function callApi(baseURL='') {
    const api = {
        token: localStorage.getItem("token"),
        async get(url, config = {}) {
            const response = await fetch(baseURL + url, {
                method: "GET",
                ...config,
                headers: getHeaders(config),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logoutUser();
                } else {
                    throw await response.json(); // Handle other errors
                }
            }

            return response.json();
        },
        async getWithToken(url, config = {}, token) {
            const response = await fetch(baseURL + url, {
                method: "GET",
                ...config,
                headers: getHeaders(config),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logoutUser();
                } else {
                    throw await response.json(); // Handle other errors
                }
            }

            return response.json();
        },
        async getText(url, config = {}) {
            const response = await fetch(baseURL + url, {
                method: "GET",
                ...config,
                headers: getHeaders(config),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logoutUser();
                } else {
                    throw await response.text();
                }
            }
            return response.text();
        },
        async post(url, data, config = {}, isFormData = false) {
            const response = await fetch(baseURL + url, {
                method: "POST",
                ...config,
                headers: getHeaders(config, isFormData),
                body: isFormData ? data : JSON.stringify(data),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logoutUser();
                    throw await response.json();
                } else {
                    throw await response.json(); // Handle other errors
                }
            }

            return response.json();
        },
        async filePost(url, data, config = {}) {
            const response = await fetch(baseURL + url, {
                method: "POST",
                ...config,
                headers: {
                    ...config.headers,
                    Authorization: `Bearer ${getToken()}`,
                },
                body: data,
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logoutUser();
                } else {
                    throw await response.json(); // Handle other errors
                }
            }

            return response.json();
        },

        async put(url, data, config = {}, isFormData) {
            const response = await fetch(baseURL + url, {
                method: "PUT",
                ...config,
                headers: getHeaders(config, isFormData),
                body: isFormData ? data : JSON.stringify(data),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logoutUser();
                } else {
                    throw await response.json(); // Handle other errors
                }
            }

            return response.json();
        },

        async delete(url, config = {}) {
            const response = await fetch(baseURL + url, {
                method: "DELETE",
                ...config,
                headers: getHeaders(config),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logoutUser();
                } else {
                    throw await response.json(); // Handle other errors
                }
            }

            return response.json();
        },
        async asyncFetchContent(endpoint, type='json') {
            const validResponseTypes = ['text', 'json'];
            const response = await fetch(endpoint);

            if (type && validResponseTypes.includes(type)) {
                return await response[type]();
            }

            return response;
        }
    };

    return api;
}
